import loadable from '@loadable/component';
import { Grid, Box, useMount } from '@simplywallst/ui-core';
import { pluralise } from '@/utilities/pluralise';
import type { Portfolio } from '@/hooks/portfolio/usePortfolio';
import { Suspense } from 'react';
import { useUserAccess } from '@/hooks/useUserAccess';
import { useHoldingsLimits } from '@/hooks/portfolio/useHoldingsLimits';
import { DividendKeyMetricsSkeleton } from './components/DividendKeyMetrics/DividendKeyMetricSkeleton';
import { DividendQualityForecast } from './components/DividendQualityForecast';
import { Sentry } from '@/components/Sentry';
import { DividendDefaultError } from '../DividendDefaultError';
import { useTracking } from 'react-tracking';

const PortfolioLimitsBanner = loadable(
  () => import('../PortfolioLimitsBanner'),
  {
    resolveComponent: (c) => c.PortfolioLimitsBanner,
  }
);

const DividendKeyMetrics = loadable(
  () => import('./components/DividendKeyMetrics'),
  {
    resolveComponent: (c) => c.DividendKeyMetrics,
    fallback: <DividendKeyMetricsSkeleton />,
  }
);

interface Props {
  portfolio: Portfolio;
}

export const DividendPanel = ({ portfolio }: Props) => {
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'dividends',
  });

  const { data: userAccess } = useUserAccess();

  const { excludedHoldings } = useHoldingsLimits({
    portfolioHoldings: portfolio?.holdings,
  });

  const isAnalysisRestricted = excludedHoldings.length > 0;

  useMount(() => {
    trackEvent({
      subject: 'page',
      modifier: 'dividendsPanel',
      action: 'landed',
    });
  });

  return (
    <Track>
      <Grid templateColumns="100vw" templateColumnsLg="auto" gap="8px">
        {isAnalysisRestricted ? (
          <Box
            marginTop="x2"
            marginTopMd="x0"
            marginBottom="x1"
            marginBottomMd="x0"
            minHeight="56px"
          >
            <PortfolioLimitsBanner
              data-cy-id="excluded-holdings-banner-holdings-panel"
              content={`${excludedHoldings.length} ${pluralise(
                excludedHoldings.length,
                'holding',
                'holdings'
              )} excluded from analysis due to ${
                userAccess.planType
              } plan limit being reached. Upgrade for full access.`}
            />
          </Box>
        ) : null}
        <Sentry
          sentryCapture={{
            team: 'dingoes',
          }}
          customCopy={<DividendDefaultError />}
          globalDisabled
          isShownLogo={false}
          isShownHeading={false}
        >
          <Suspense fallback={<DividendKeyMetricsSkeleton />}>
            <DividendKeyMetrics />
          </Suspense>
        </Sentry>
        <DividendQualityForecast />
      </Grid>
    </Track>
  );
};
